import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, SkipBack, SkipForward, Volume2, Music } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { api, mediaUrl, sessionId } from "@/lib/api";
import { useNowPlaying } from "@/hooks/useNowPlaying";
import NowPlayingBadge from "@/components/NowPlayingBadge";
import { motion, AnimatePresence } from "framer-motion";

function fmt(s) {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}

export default function MusicPlayerBar() {
  const { current, isPlaying, setIsPlaying, togglePlay, next, prev } = usePlayer();
  const audioRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [plays, setPlays] = useState(0);
  const countedRef = useRef(false);
  const nowPlaying = useNowPlaying();

  // Live listener heartbeat while a track is actively playing
  useEffect(() => {
    if (!current || !isPlaying) return;
    const sid = sessionId();
    const beat = () => api.post(`/tracks/${current.id}/heartbeat`, { session_id: sid }).catch(() => {});
    beat();
    const i = setInterval(beat, 10000);
    return () => { clearInterval(i); api.post("/tracks/heartbeat/stop", { session_id: sid }).catch(() => {}); };
  }, [current, isPlaying]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a || !current) return;
    a.src = mediaUrl(current.audio_url);
    a.load();
    if (isPlaying) a.play().catch(() => {});
    setPlays(current.plays || 0);
    countedRef.current = false; // reset play-count gate for the new track
  }, [current]); // eslint-disable-line

  const handleTimeUpdate = (e) => {
    const t = e.target.currentTime;
    setProgress(t);
    // Only count a play once the listener passes ~30s (or 90% of short tracks)
    if (!countedRef.current && current && duration) {
      const threshold = Math.min(30, duration * 0.9);
      if (t >= threshold) {
        countedRef.current = true;
        api.post(`/tracks/${current.id}/play`)
          .then(({ data }) => { setPlays(data.plays); window.dispatchEvent(new Event("nu:charts")); })
          .catch(() => {});
      }
    }
  };

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (isPlaying) a.play().catch(() => setIsPlaying(false));
    else a.pause();
  }, [isPlaying, setIsPlaying]);

  useEffect(() => { if (audioRef.current) audioRef.current.volume = volume; }, [volume]);

  const onSeek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    a.currentTime = pct * duration;
  };

  return (
    <>
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onEnded={next}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />
      <AnimatePresence>
        {current && (
          <motion.div
            data-testid="music-player-bar"
            initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 30 }}
            className="fixed bottom-0 left-0 right-0 z-40 bg-[#0A0A0E]/95 backdrop-blur-lg border-t border-white/10 shadow-2xl">
            <div
              className="h-1 bg-white/10 cursor-pointer group" onClick={onSeek} data-testid="player-progress">
              <div className="h-full bg-emerald-500 relative" style={{ width: `${(progress / (duration || 1)) * 100}%` }}>
                <span className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-emerald-400 opacity-0 group-hover:opacity-100" />
              </div>
            </div>
            <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {current.cover_url ? (
                  <img src={mediaUrl(current.cover_url)} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
                ) : (
                  <span className="w-12 h-12 rounded-lg bg-violet-500/20 flex items-center justify-center shrink-0"><Music className="w-5 h-5 text-violet-300" /></span>
                )}
                <div className="min-w-0">
                  <p className="font-bold text-sm truncate" data-testid="player-track-title">{current.title}</p>
                  <p className="text-xs text-zinc-400 truncate flex items-center gap-2">
                    {current.artist?.artist_name}
                    <span className="text-zinc-600 font-mono-nu flex items-center gap-1" data-testid="player-play-count"><Play className="w-3 h-3" /> {plays}</span>
                    <NowPlayingBadge count={nowPlaying[current.id]} />
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 sm:gap-4">
                <button onClick={prev} className="text-zinc-400 hover:text-white transition-colors" data-testid="player-prev"><SkipBack className="w-5 h-5" /></button>
                <button onClick={togglePlay} data-testid="player-toggle"
                  className="w-11 h-11 rounded-full bg-emerald-500 hover:bg-emerald-600 text-black flex items-center justify-center transition-colors">
                  {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                </button>
                <button onClick={next} className="text-zinc-400 hover:text-white transition-colors" data-testid="player-next"><SkipForward className="w-5 h-5" /></button>
              </div>

              <div className="hidden sm:flex items-center gap-3 flex-1 justify-end">
                <span className="font-mono-nu text-xs text-zinc-500">{fmt(progress)} / {fmt(duration)}</span>
                <div className="hidden lg:flex items-center gap-2 w-28">
                  <Volume2 className="w-4 h-4 text-zinc-400" />
                  <input type="range" min="0" max="1" step="0.01" value={volume}
                    onChange={(e) => setVolume(parseFloat(e.target.value))}
                    className="w-full accent-emerald-500" data-testid="player-volume" />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
